/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useRef
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import useHasMounted from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {
  GenericObject
} from '@ion/core/src/types/entities';
import {GridDrawer} from '@ion/dls/src/components/drawer/GridDrawer';
import {useExportProgress} from '../../hooks/useExportProgress';
import {ExportContext} from '../../types/redux';
import {PageTitle} from '../admin/AdminContainer';
import JobStatusGrid from './JobStatusGrid';
import {
  initializeJobStatus,
  setJobStatus
} from './state';
import {showJobStatusDrawerSelector} from './state-selectors';
import ViewJobStatus from './ViewJobStatus';

export interface JobDrawerProps extends TaskStatusGridEmailProps {
  pageTitle?: PageTitle;
  selectedJobId?: Function;
}

export interface TaskStatusGridEmailProps {
  hasEmailExport?: boolean;
};

/**
 * Defines the JobStatus container component. This component is only to render
 * child components and provide top level subscriptions and dispatches to the
 * redux store.
 */
export const JobStatusDrawer = ({
  pageTitle,
  selectedJobId,
  hasEmailExport = false
}: JobDrawerProps) => {
  const dispatch    = useDispatch();
  const isMounted   = useHasMounted();
  const isExporting = useExportProgress(ExportContext.jobStatus);
  const gridRef     = useRef<GenericObject>();

  const {
    showTaskStatus,
    taskStatus
  } = useSelector(({jobStatus}) => jobStatus);
  const open = useSelector(showJobStatusDrawerSelector);

  useEffect(() => {
    if (!isMounted) {
      dispatch(initializeJobStatus());
    }
  }, [isMounted]);

  const onExport = useCallback(() => {
    gridRef.current.onExport();
  }, []);

  const onRefresh = useCallback(() => {
    gridRef.current?.onRefresh(true);
  }, []);

  const onClose = useCallback(function(): void {
    dispatch(setJobStatus(false));
  }, []);

  return (
    <>
      <GridDrawer open={open}
        isExporting={isExporting}
        title={pageTitle || translate('jobstatus:name') || PageTitle.jobStatus}
        onClose={onClose}
        onGridRefresh={onRefresh}
        onGridExport={onExport}>
        <JobStatusGrid ref={gridRef} selectedJobId={selectedJobId} />
      </GridDrawer>
      {showTaskStatus && <ViewJobStatus jobStatus={taskStatus}
        hasEmailExport={hasEmailExport}
        open={showTaskStatus} />}
    </>
  );
};

export default JobStatusDrawer;
